import React, { useState } from 'react';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import ConfirmPopup from '../ConfirmPopup/ConfirmPopup';
import Toast from '../../components/support/Toast';
import IconSave from '@mui/icons-material/SaveAltOutlined';
import IconDelete from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { TextField } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { DataProviderInstance } from '../../api/DataProvider';
import MealEditor from './MealEditor';
import BodyMeasurementsHint from './BodyMeasurementsHint';
import styles from './MealPlanPopup.module.css';
import {
  MAX_MEALS,
  cleanMeals,
  cloneMeals,
  createMeal,
  defaultMeals,
  newKey,
  normalizeMeals,
  validatePlan,
} from '../../pages/appointment/components/mealPlan/mealPlanModel';

export const POPUP_MODE_CREATE = 'create';
export const POPUP_MODE_EDIT = 'edit';
export const POPUP_MODE_VERSION = 'version';

const TITLES = {
  [POPUP_MODE_CREATE]: 'Novo plano alimentar',
  [POPUP_MODE_EDIT]: 'Editar plano alimentar',
  [POPUP_MODE_VERSION]: 'Nova versão do plano',
};

/**
 * Montagem e edição do plano alimentar, como um documento sendo escrito:
 * título e início no topo, refeições em blocos (cabeçalho com horário e
 * nome, corpo com os alimentos) e orientações gerais no fim.
 *
 * `mode`: create (plano novo do zero), edit (altera o plano `plan` no lugar)
 * ou version (plano novo deste atendimento partindo de `plan`, que vai para o
 * histórico).
 */
function MealPlanPopup({ mode, plan, patientId, appointmentId, bodyMeasurements, OnHide }) {
  const { SetPatientMealPlan, UpdatePatientMealPlan, DeletePatientMealPlan } = DataProviderInstance();

  const isEdit = mode === POPUP_MODE_EDIT;
  const isVersion = mode === POPUP_MODE_VERSION;

  const [title, setTitle] = useState(plan?.title || '');
  const [startsAt, setStartsAt] = useState(isEdit && plan?.startsAt ? plan.startsAt : null);
  const [guidelines, setGuidelines] = useState(plan?.guidelines || '');
  const [meals, setMeals] = useState(() => {
    if (isEdit) return normalizeMeals(plan?.meals);
    if (isVersion) return cloneMeals(plan?.meals);
    return defaultMeals();
  });
  const [saving, setSaving] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });

  const showToast = (message, severity = 'error') => setToast({ open: true, message, severity });
  const canAddMeal = meals.length < MAX_MEALS;

  function updateMeal(key, nextMeal) {
    setMeals((current) => current.map((meal) => (meal.key === key ? nextMeal : meal)));
  }

  function moveMeal(index, direction) {
    setMeals((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function duplicateMeal(index) {
    setMeals((current) => {
      if (current.length >= MAX_MEALS) return current;
      const copy = cloneMeals([current[index]])[0];
      copy.name = copy.name ? `${copy.name} (cópia)` : '';
      copy.key = newKey('m');
      const next = [...current];
      next.splice(index + 1, 0, copy);
      return next;
    });
  }

  function removeMeal(key) {
    setMeals((current) => current.filter((meal) => meal.key !== key));
  }

  function addMeal() {
    setMeals((current) => (current.length >= MAX_MEALS ? current : [...current, createMeal()]));
  }

  function buildPayload() {
    const cleaned = cleanMeals(meals);
    const error = validatePlan(cleaned);
    if (error) {
      showToast(error);
      return null;
    }
    return {
      title: title.trim(),
      startsAt: startsAt ? dayjs(startsAt).format('YYYY-MM-DD') : '',
      meals: cleaned,
      guidelines: guidelines.trim(),
      previousPlanId: isVersion && plan ? plan.id : null,
    };
  }

  function saveHandler() {
    if (saving) return;
    const payload = buildPayload();
    if (!payload) return;

    setSaving(true);
    const done = (response) => {
      setSaving(false);
      if (response && response.status === 'SUCCESS') {
        OnHide(true);
      } else {
        showToast(response?.error || 'Não foi possível salvar o plano.');
      }
    };

    if (isEdit) {
      UpdatePatientMealPlan(plan.id, payload, done);
    } else {
      SetPatientMealPlan(patientId, appointmentId, payload, done);
    }
  }

  function deleteHandler() {
    DeletePatientMealPlan(plan.id, (response) => {
      if (response && response.status === 'SUCCESS') {
        OnHide(true);
      } else {
        showToast('Não foi possível excluir o plano.');
      }
    });
  }

  const content = (
    <div className={styles.container}>
      <div className={styles.top}>
        <div className={styles.header}>
          <TextField
            size="small"
            label="Título do plano"
            placeholder="Ex.: Plano de reeducação alimentar"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 120 }}
            className={styles.headerTitle}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Início"
              value={startsAt ? dayjs(startsAt) : null}
              format="DD/MM/YYYY"
              onChange={(value) => setStartsAt(value ? value.format('YYYY-MM-DD') : null)}
              slotProps={{ textField: { size: 'small', className: styles.headerDate } }}
            />
          </LocalizationProvider>
        </div>
        <BodyMeasurementsHint bodyMeasurements={bodyMeasurements} />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHead}>
          <span className={styles.sectionLabel}>Refeições</span>
          <span className={styles.sectionCount}>
            {meals.length} {meals.length === 1 ? 'refeição' : 'refeições'}
          </span>
        </div>
        <div className={styles.meals}>
          {meals.map((meal, index) => (
            <MealEditor
              key={meal.key}
              meal={meal}
              index={index}
              total={meals.length}
              onChange={(nextMeal) => updateMeal(meal.key, nextMeal)}
              onMoveUp={() => moveMeal(index, -1)}
              onMoveDown={() => moveMeal(index, 1)}
              onDuplicate={() => duplicateMeal(index)}
              onRemove={() => removeMeal(meal.key)}
            />
          ))}
          <button type="button" className={styles.addMeal} onClick={addMeal} disabled={!canAddMeal}>
            <AddIcon />
            Adicionar refeição
          </button>
        </div>
      </div>

      <div className={styles.section}>
        <span className={styles.sectionLabel}>Orientações gerais</span>
        <TextField
          size="small"
          value={guidelines}
          onChange={(e) => setGuidelines(e.target.value)}
          placeholder="Água, horários, o que evitar: o que vale para o dia todo"
          inputProps={{ 'aria-label': 'Orientações gerais' }}
          multiline
          minRows={3}
          fullWidth
        />
      </div>

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      {isEdit && (
        <CustomButton
          variant="contained"
          style="error"
          label="Excluir"
          icon={<IconDelete />}
          onClick={() => setShowConfirmDelete(true)}
          enabled={!saving}
          className={styles.footerDelete}
        />
      )}

      <CustomButton
        variant="outlined"
        style="secondary"
        label="Cancelar"
        onClick={() => OnHide(false)}
        enabled={!saving}
      />

      <CustomButton
        style="primary"
        variant="contained"
        label={isEdit ? 'Atualizar' : 'Salvar plano'}
        icon={<IconSave />}
        onClick={saveHandler}
        loading={saving}
        enabled={!saving}
      />

      {showConfirmDelete && (
        <ConfirmPopup
          Visibility={showConfirmDelete}
          Title="Confirmar exclusão"
          Description="Tem certeza que deseja excluir este plano alimentar? Se ele for o vigente, a versão anterior volta a valer."
          ConfirmLabel="Excluir"
          OnConfirm={deleteHandler}
          OnHide={() => setShowConfirmDelete(false)}
        />
      )}
    </div>
  );

  return (
    <PopupBase
      Title={TITLES[mode] || TITLES[POPUP_MODE_CREATE]}
      Content={content}
      Footer={footer}
      OnClose={() => OnHide(false)}
      maxWidth="md"
    />
  );
}

export default MealPlanPopup;
